import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PagePanel from 'src/components/PagePanel';
import PageHeader from 'src/components/PageHeader';
import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  fetchManualTransactions,
  formatManualOwnerName,
  getManualOwner,
  isManualExecuted,
  type ManualTransaction,
  type ManualTransactionKind,
} from 'src/api/adminManualTransactions';
import { convertDate } from 'src/components/Table/helpers';
import { MANUAL_TRANSACTIONS_HEADERS } from 'src/utils/constants';

import ManualTransactionModal from './ManualTransactionModal';
import { StatusText } from './styled';

const statusTone = (raw?: string): 'positive' | 'negative' | 'muted' | 'warning' => {
  switch (String(raw ?? '').toUpperCase()) {
    case 'SUCCESS':
      return 'positive';
    case 'FAILED':
      return 'negative';
    case 'PENDING':
      return 'warning';
    default:
      return 'muted';
  }
};

type ManualTransactionsPageProps = {
  kind: ManualTransactionKind;
};

/**
 * Both manual queues are the same screen against a different endpoint, so one
 * component serves the deposit and withdrawal routes.
 */
const ManualTransactionsPage: React.FC<ManualTransactionsPageProps> = ({ kind }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [transactions, setTransactions] = useState<ManualTransaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ManualTransaction | null>(null);

  const dispatch = useDispatch();

  const title = kind === 'deposit' ? 'Manual Deposits' : 'Manual Withdrawals';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchManualTransactions(kind, { page: currentPage, limit: 10 });
      setTransactions(res.transactions);
      setTotalCount(res.meta?.total_count ?? 0);
      setTotalPages(Math.max(1, res.meta?.page_count || 1));
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? `Failed to load manual ${kind}s`,
          isSuccess: false,
        }),
      );
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [kind, currentPage, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // The endpoint takes page and limit only — no status filter, no sort — so the
  // list is newest-first and paged, and that is all it can offer.
  const rows = useMemo(
    () =>
      transactions.map((tx) => {
        const created = tx.created_at ? convertDate(tx.created_at) : null;
        return {
          ...tx,
          user_display: formatManualOwnerName(getManualOwner(tx)),
          amount_display: `${tx.quantity ?? '—'} ${tx.currency_code ?? ''}`.trim(),
          executed_display: isManualExecuted(tx) ? 'Executed' : 'Awaiting decision',
          created_display: created ? `${created.date} ${created.time}` : '—',
        };
      }),
    [transactions],
  );

  return (
    <PagePanel>
      <PageHeader title={`${title} (${totalCount})`} hideSearch />

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={MANUAL_TRANSACTIONS_HEADERS}
          items={rows}
          columns="1.2fr 1fr 0.9fr 1fr 1.1fr"
          fieldsToShow={[
            'user_display',
            'amount_display',
            'status',
            'executed_display',
            'created_display',
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          onRowClick={(item) => setSelected(item as ManualTransaction)}
          renderCell={(item, field) => {
            if (field === 'status') {
              return (
                <StatusText $tone={statusTone(item.status)}>{item.status || '—'}</StatusText>
              );
            }
            if (field === 'executed_display') {
              return (
                <StatusText $tone={item.executed_display === 'Executed' ? 'muted' : 'warning'}>
                  {item.executed_display}
                </StatusText>
              );
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}

      {selected && (
        <ManualTransactionModal
          kind={kind}
          transaction={selected}
          onClose={() => setSelected(null)}
          onExecuted={load}
        />
      )}
    </PagePanel>
  );
};

export default ManualTransactionsPage;
